import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  TextField,
  Box,
  CircularProgress,
  Avatar,
} from '@mui/material';
import { IoClose, IoSend, IoChatbubble } from 'react-icons/io5';
import VuiBox from 'components/VuiBox';
import VuiTypography from 'components/VuiTypography';
import VuiButton from 'components/VuiButton';
import { useHistory, useLocation } from 'react-router-dom';

const Chatbot = ({ open, onClose }) => {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: 'Hello! I\'m your AI assistant. I can help you navigate through the dashboard and access our services like Restricted Zone Monitor, PPE Detection Dashboard, Lost and Found, Theft Detection, Fire Detection, Document Archiving, Fall Detection, Attendance, and AI Inventory Agent.\n\n💡 Try asking me:\n• "Open fall detection"\n• "Take me to document archiving"\n• "Show attendance"\n• "List services"\n\nHow can I help you today?',
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const history = useHistory();
  const location = useLocation();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const services = [
    { name: 'Restricted Zone Monitor', path: '/restricted-zone-monitor', keywords: ['restricted zone'] },
    { name: 'PPE Detection Dashboard', path: '/ppe-detection-dashboard', keywords: ['ppe detection', 'ppe dashboard', 'safety equipment'] },
    { name: 'Lost and Found', path: '/lost-and-found', keywords: ['lost and found', 'lost found'] },
    { name: 'Theft Detection', path: '/theft-detection', keywords: ['theft detection', 'theft'] },
    { name: 'Fire Detection', path: '/fire-detection', keywords: ['fire detection', 'fire'] },
    { name: 'Document Archiving', path: '/document-archiving', keywords: ['document archiving', 'document archive', 'archiving'] },
    { name: 'Fall Detection', path: '/fall-detection', keywords: ['fall detection', 'fall'] },
    { name: 'AI Inventory Agent', path: '/ai-inventory-agent', keywords: ['ai inventory', 'inventory agent', 'inventory', '2xl'] },
    { name: 'Attendance', path: '/attendance', keywords: ['attendance', 'check in', 'attendance system'] },
    { name: 'Dashboard', path: '/dashboard', keywords: ['home', 'dashboard', 'main'] },
  ];

  const detectServiceIntent = (userMessage) => {
    const lowerMessage = userMessage.toLowerCase();
    
    for (const service of services) {
      if (service.keywords.some(keyword => lowerMessage.includes(keyword))) {
        return service;
      }
    }
    return null;
  };

  const getLocalResponse = (userMessage) => {
    const lowerMessage = userMessage.toLowerCase();
    
    // Greetings
    if (/^(hi|hello|hey|good morning|good afternoon|good evening)$/i.test(lowerMessage.trim())) {
      return "Hello! 👋 I'm your AI assistant. I can help you navigate through the dashboard and access our services. Try asking me to:\n\n• Open any service (Fall Detection, Fire Detection, etc.)\n• Navigate to different pages\n• List available services\n\nWhat would you like to do?";
    }
    
    // Help/What can you do
    if (lowerMessage.includes('help') || lowerMessage.includes('what can you do') || lowerMessage.includes('how can you help')) {
      return "I can help you with:\n\n✅ Navigate to any service (just say 'open [service name]')\n✅ Quick access to all dashboard features\n\nAvailable services:\n• Fall Detection\n• Fire Detection\n• Theft Detection\n• PPE Detection\n• Document Archiving\n• Lost and Found\n• Restricted Zone Monitor\n• AI Inventory Agent\n\nJust tell me what you need!";
    }
    
    // List services
    if (lowerMessage.includes('list services') || lowerMessage.includes('show services') || lowerMessage.includes('available services')) {
      return "Here are all available services:\n\n👤 Fall Detection - Detect falls and accidents\n🔥 Fire Detection - Fire and smoke detection\n🚨 Theft Detection - Security monitoring\n🦺 PPE Detection - Safety equipment monitoring\n📄 Document Archiving - Document management\n🔍 Lost and Found - Track lost items\n🚫 Restricted Zone Monitor - Access control\n📦 AI Inventory Agent - Smart inventory management\n\nJust say 'open [service name]' to navigate!";
    }
    
    // Thank you
    if (lowerMessage.includes('thank') || lowerMessage.includes('thanks')) {
      return "You're welcome! Let me know if you need anything else. 😊";
    }
    
    return null; // No local match, will call API
  };

  const handleSendMessage = async () => {
    if (!input.trim()) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    // Check if user wants to navigate to a service
    const detectedService = detectServiceIntent(userMessage);

    try {
      // If a service was detected, navigate directly
      if (detectedService) {
        setMessages(prev => [
          ...prev,
          {
            role: 'assistant',
            content: `Opening ${detectedService.name} for you now...`,
          },
        ]);
        setTimeout(() => {
          handleNavigate(detectedService.path);
        }, 800);
        setLoading(false);
        return;
      }

      // Local response handler for common queries
      const localResponse = getLocalResponse(userMessage);
      if (localResponse) {
        setMessages(prev => [...prev, { role: 'assistant', content: localResponse }]);
        setLoading(false);
        return;
      }

      // Regular AI response for other queries
      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer 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',
        },
        body: JSON.stringify({
          model: 'gpt-4',
          messages: [
            {
              role: 'system',
              content: `You are a helpful AI assistant for a smart dashboard system. You help users navigate through different services including Fall Detection, Fire Detection, Theft Detection, PPE Detection, Document Archiving, Lost and Found, Restricted Zone Monitor, and AI Inventory Agent. Be concise and friendly.`,
            },
            ...messages.map(msg => ({
              role: msg.role,
              content: msg.content,
            })),
            { role: 'user', content: userMessage },
          ],
          temperature: 0.7,
          max_tokens: 500,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to get response from AI');
      }

      const data = await response.json();
      const assistantMessage = data.choices[0].message.content;

      setMessages(prev => [...prev, { role: 'assistant', content: assistantMessage }]);
    } catch (error) {
      console.error('Error:', error);
      setMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          content: 'I apologize, but I encountered an error. Please try again.',
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleNavigate = (path) => {
    history.push(path);
    onClose();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          position: 'fixed',
          bottom: '100px',
          right: '20px',
          margin: 0,
          maxWidth: '450px',
          width: '450px',
          background: 'linear-gradient(127.09deg, rgba(6, 11, 40, 0.94) 19.41%, rgba(10, 14, 35, 0.94) 76.65%)',
          backdropFilter: 'blur(42px)',
          borderRadius: '20px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0px 20px 60px rgba(0, 0, 0, 0.5)',
        },
      }}
      sx={{
        '& .MuiDialog-container': {
          alignItems: 'flex-end',
          justifyContent: 'flex-end',
        },
        '& .MuiBackdrop-root': {
          backgroundColor: 'transparent',
        },
      }}
    >
      <DialogTitle
        sx={{
          background: '#0055cc',
          color: 'white',
          padding: '20px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <IoChatbubble size={24} />
          <VuiTypography variant="h6" color="white" fontWeight="bold">
            AI Assistant
          </VuiTypography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            color: 'white',
            '&:hover': { background: 'rgba(255, 255, 255, 0.1)' },
          }}
        >
          <IoClose />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: 0, height: '550px', display: 'flex', flexDirection: 'column' }}>
        {/* Messages Container */}
        <VuiBox
          sx={{
            flex: 1,
            overflowY: 'auto',
            padding: '20px',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'rgba(10, 14, 35, 0.6)',
              borderRadius: '10px',
              margin: '4px 0',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#0055cc',
              borderRadius: '10px',
              border: '2px solid rgba(10, 14, 35, 0.6)',
              transition: 'all 0.3s ease',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#0066dd',
              border: '2px solid rgba(10, 14, 35, 0.4)',
              boxShadow: '0 0 10px rgba(67, 24, 255, 0.5)',
            },
            '&::-webkit-scrollbar-thumb:active': {
              background: '#0044aa',
            },
          }}
        >
          {messages.map((message, index) => (
            <Box key={index} sx={{ marginBottom: '16px' }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
                  alignItems: 'flex-start',
                  gap: '12px',
                }}
              >
                {message.role === 'assistant' && (
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      background: '#0055cc',
                    }}
                  >
                    <IoChatbubble size={18} />
                  </Avatar>
                )}
                <Box
                  sx={{
                    maxWidth: '75%',
                    padding: '12px 16px',
                    borderRadius: '12px',
                    background:
                      message.role === 'user'
                        ? '#4a5568'
                        : 'rgba(255, 255, 255, 0.05)',
                    border: message.role === 'assistant' ? '1px solid rgba(255, 255, 255, 0.1)' : 'none',
                  }}
                >
                  <VuiTypography variant="body2" color="white" sx={{ whiteSpace: 'pre-wrap' }}>
                    {message.content}
                  </VuiTypography>
                </Box>
              </Box>
            </Box>
          ))}
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: '12px' }}>
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  background: '#0055cc',
                }}
              >
                <IoChatbubble size={18} />
              </Avatar>
              <Box
                sx={{
                  padding: '12px 16px',
                  borderRadius: '12px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                }}
              >
                <CircularProgress size={20} sx={{ color: '#0055cc' }} />
              </Box>
            </Box>
          )}
          <div ref={messagesEndRef} />
        </VuiBox>

        {/* Input Container */}
        <VuiBox
          sx={{
            padding: '20px',
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            background: 'rgba(0, 0, 0, 0.2)',
          }}
        >
          <Box sx={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
            <TextField
              fullWidth
              multiline
              maxRows={3}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your message..."
              disabled={loading}
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: 'white',
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '12px',
                  '& fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#0055cc',
                  },
                },
                '& .MuiInputBase-input': {
                  color: 'white',
                  '&::placeholder': {
                    color: 'rgba(255, 255, 255, 0.5)',
                    opacity: 1,
                  },
                },
              }}
            />
            <IconButton
              onClick={handleSendMessage}
              disabled={!input.trim() || loading}
              sx={{
                background: '#0055cc',
                color: 'white',
                width: '48px',
                height: '48px',
                '&:hover': {
                  background: '#0044aa',
                },
                '&:disabled': {
                  background: 'rgba(255, 255, 255, 0.1)',
                  color: 'rgba(255, 255, 255, 0.3)',
                },
              }}
            >
              <IoSend />
            </IconButton>
          </Box>
        </VuiBox>
      </DialogContent>
    </Dialog>
  );
};

export default Chatbot;

